/**
 * AURA Frontend Auth — Clerk Integration
 *
 * Clerk handles authentication (sign-in, sign-up, session, JWT tokens).
 * Supabase handles the database (settings, profiles, orgs).
 *
 * Clerk provides:
 * - Pre-built sign-in/sign-up UI (mountable components)
 * - Session management with automatic token refresh
 * - JWT tokens for backend API auth
 * - Organization management
 */

import { Clerk } from '@clerk/clerk-js';
import { setAccessToken } from './client';

// ============================================================================
// Configuration
// ============================================================================

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '';

// ============================================================================
// Client
// ============================================================================

let clerk: Clerk | null = null;

export async function getClerk(): Promise<Clerk | null> {
  if (!CLERK_PUBLISHABLE_KEY) return null;
  if (!clerk) {
    clerk = new Clerk(CLERK_PUBLISHABLE_KEY);
    await clerk.load();
  }
  return clerk;
}

// ============================================================================
// Auth State
// ============================================================================

export interface AuthState {
  userId: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

let authState: AuthState = {
  userId: null,
  email: null,
  isAuthenticated: false,
  isLoading: true,
};

const listeners: Array<(state: AuthState) => void> = [];

function updateAuthState(updates: Partial<AuthState>): void {
  authState = { ...authState, ...updates };
  listeners.forEach((fn) => fn(authState));
}

export function onAuthChange(listener: (state: AuthState) => void): () => void {
  listeners.push(listener);
  listener(authState);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

export function getAuthState(): AuthState {
  return authState;
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize auth. Returns current state.
 * If Clerk is configured, loads client and checks for existing session.
 * If not configured, returns anonymous mode.
 */
export async function initAuth(): Promise<AuthState> {
  if (!CLERK_PUBLISHABLE_KEY) {
    updateAuthState({ isLoading: false, isAuthenticated: false });
    return authState;
  }

  try {
    const clerkClient = await getClerk();
    if (!clerkClient) {
      updateAuthState({ isLoading: false });
      return authState;
    }

    // Check for existing session
    if (clerkClient.user) {
      const token = await clerkClient.session?.getToken();
      if (token) setAccessToken(token);

      updateAuthState({
        userId: clerkClient.user.id,
        email: clerkClient.user.primaryEmailAddress?.emailAddress ?? null,
        isAuthenticated: true,
        isLoading: false,
      });
    } else {
      updateAuthState({ isLoading: false });
    }

    // Listen for session changes
    clerkClient.addListener(({ user, session }) => {
      if (user && session) {
        session.getToken().then((token) => {
          if (token) setAccessToken(token);
        });
        updateAuthState({
          userId: user.id,
          email: user.primaryEmailAddress?.emailAddress ?? null,
          isAuthenticated: true,
        });
      } else {
        setAccessToken(null);
        updateAuthState({
          userId: null,
          email: null,
          isAuthenticated: false,
        });
      }
    });
  } catch (err) {
    console.error('Clerk init failed:', err);
    updateAuthState({ isLoading: false });
  }

  return authState;
}

// ============================================================================
// Sign-In / Sign-Up UI (Clerk mounted components)
// ============================================================================

/**
 * Mount Clerk's sign-in component into a DOM element.
 * Clerk provides a polished, configurable sign-in form out of the box.
 */
export async function mountSignIn(element: HTMLElement): Promise<void> {
  const clerkClient = await getClerk();
  if (!clerkClient) return;

  clerkClient.mountSignIn(element as HTMLDivElement, {
    appearance: {
      variables: {
        colorPrimary: 'rgba(255,255,255,0.8)',
        colorBackground: '#0a0a12',
        colorText: 'rgba(255,255,255,0.7)',
        colorTextSecondary: 'rgba(255,255,255,0.3)',
        colorInputBackground: 'rgba(255,255,255,0.03)',
        colorInputText: 'rgba(255,255,255,0.8)',
        borderRadius: '4px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        fontSize: '12px',
      },
      elements: {
        card: { background: 'transparent', boxShadow: 'none', border: 'none' },
        headerTitle: {
          fontSize: '11px', fontWeight: '400',
          letterSpacing: '0.12em', textTransform: 'uppercase' as const,
          color: 'rgba(255,255,255,0.35)',
        },
        headerSubtitle: { display: 'none' },
        formButtonPrimary: {
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          fontSize: '11px', fontWeight: '500',
          letterSpacing: '0.06em', textTransform: 'uppercase' as const,
        },
        footerActionLink: {
          color: 'rgba(255,255,255,0.25)',
          fontSize: '10px',
        },
        socialButtonsBlockButton: {
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(255,255,255,0.5)',
        },
      },
    },
  });
}

/**
 * Unmount Clerk sign-in from an element.
 */
export async function unmountSignIn(element: HTMLElement): Promise<void> {
  const clerkClient = await getClerk();
  if (!clerkClient) return;
  clerkClient.unmountSignIn(element as HTMLDivElement);
}

// ============================================================================
// Sign Out
// ============================================================================

export async function signOut(): Promise<void> {
  const clerkClient = await getClerk();
  if (!clerkClient) return;
  await clerkClient.signOut();
}

// ============================================================================
// Token
// ============================================================================

/**
 * Get a fresh JWT token for API calls.
 * Call this before each API request if the token might be stale.
 */
export async function getToken(): Promise<string | null> {
  const clerkClient = await getClerk();
  if (!clerkClient?.session) return null;
  return clerkClient.session.getToken();
}

// ============================================================================
// Auth Check
// ============================================================================

export function isAuthConfigured(): boolean {
  return Boolean(CLERK_PUBLISHABLE_KEY);
}
